import type { DateTime } from 'luxon'
import { action, makeObservable, observable } from 'mobx'
import type {
  AssignmentGroupingType,
  FirestoreSectionAssignment,
} from '../../../firestore/SectionAssignment'
import {
  assertCanPublishAssignments,
  createSectionAssignment,
} from '../../../firestore/SectionAssignment'
import {
  type AssignmentLimitReachedError,
  isAssignmentLimitReachedError,
} from '../../../sources-of-truth/assignmentLimit'
import { setPeerReviewQuestionsForAssignment } from '../../../firestore/SectionAssignmentPeerReviewQuestion'
import { AssignmentType } from '../../../types'
import type { AssignNextbookCubit } from '../AssignNextbookCubit'
import { schema, schemaForLti } from '../schemas'
import type { AssignNextbookLtiOutput, GradingScalars } from '../types'
import { SimpleObservableValue } from '../../../stores/SimpleObservableValue'
import { sum } from '../../../util/arrays'

export class AssignNextbookProcessingController {
  cubit: AssignNextbookCubit

  @observable
  status = new SimpleObservableValue<'input' | 'processing' | 'complete'>(
    'input'
  )

  /**
   * Set when the plan has more assignments than the section pass has room
   * for (BL-5134). Nothing is created in that case.
   */
  @observable
  limitError = new SimpleObservableValue<AssignmentLimitReachedError | null>(
    null
  )

  constructor(cubit: AssignNextbookCubit) {
    this.cubit = cubit
    makeObservable(this)
  }

  @action
  async processLTI() {
    const callback = this.cubit.ltiCallback
    const canProcessLti = this.cubit.ltiModeOn && callback !== undefined

    if (!canProcessLti) return false

    this.status.setValue('processing')

    const fields = this.cubit.form.fields
    const fieldsObject = Object.fromEntries(fields.map.entries())
    const strictFields = schemaForLti.safeParse(fieldsObject)

    if (!strictFields.success) {
      console.error('Failed to parse fields:', strictFields.error)
      this.status.setValue('input')
      return true
    }

    const input = strictFields.data

    const assignments = input.assignments
    const groupingOptions = input.groupingOptions
    const peerReviewOptions = input.peerReviewOptions
    const gradingOptions = input.gradingOptions

    const items: AssignNextbookLtiOutput[] = []
    for (const assignment of assignments) {
      const slideDeckId = assignment.slideDeckId
      const slideDeckName =
        this.cubit.teachingPlanCubit.slideDecksById.get(slideDeckId)?.data
          .slideDeckName ?? ''

      const constraints =
        this.cubit.schedule.groupSizeConstraintsBySlideDeckId.get(
          assignment.slideDeckId
        )

      // Role constraints are a floor, not a replacement — getGroupSizeConstraintsFromRoles
      // returns {min:1} for role-less decks, which would otherwise silently clobber the
      // instructor's chosen minimum.
      const groupingSizeMinimum: number = Math.max(
        constraints?.min ?? 1,
        groupingOptions.groupingSizeMinimum
      )
      const groupingSizeMaximum: number =
        assignment.groupSizeOverride ?? groupingOptions.groupingSizeMaximum
      const groupingSize: number =
        assignment.groupSizeOverride ?? groupingOptions.groupingSize

      // if we allowed peer review globally, but override an assignment with role
      // constraints to a grouping size of 1, disable peer review for that assignment
      const peerReviewEnabled =
        peerReviewOptions?.peerReviewsEnabled && groupingSize > 1

      const options = withoutUndefined({
        groupingType: groupingOptions.groupingType,
        groupingSize,
        groupingSizeMinimum,
        groupingSizeMaximum,
        forbidStudentGroupCreation: groupingOptions.forbidStudentGroupCreation,
        forbidStudentGroupScheduling:
          groupingOptions.forbidStudentGroupScheduling,
        forbidStudentGroupJoining: groupingOptions.forbidStudentGroupJoining,
        forbidStudentGroupLeaving: groupingOptions.forbidStudentGroupLeaving,
        scalars: gradingOptions.configureGrading
          ? this.buildCorrectScalars(
              assignment.slideDeckId,
              gradingOptions.scalars
            )
          : undefined,
        completionMetric: gradingOptions.configureGrading
          ? gradingOptions.completionMetric
          : undefined,
        organizationId: this.cubit.ltiOptions?.organizationId,
        assignedAt: assignment.startTime.toISO() || undefined,
        expiresAt: assignment.endTime.toISO() || undefined,
        peerReviewsEnabled: peerReviewEnabled,
        peerReviewCriteria: peerReviewEnabled
          ? peerReviewOptions.peerReviewCriteria
          : undefined,
        peerReviewExcludesSelfReview: peerReviewEnabled
          ? peerReviewOptions.peerReviewExcludesSelfReview
          : undefined,
        peerReviewsHiddenFromReviewees: peerReviewEnabled
          ? peerReviewOptions.peerReviewsHiddenFromReviewees
          : undefined,
      })

      const params = {
        sectionId: '',
        slideDeckName,
        slideDeckId: assignment.slideDeckId,
        catalogId: assignment.catalogId,
        options: options,
      }

      items.push(params)
    }

    callback(items)
    this.cubit.storage.clear()

    return true
  }

  @action
  async process() {
    const processedByLti = await this.processLTI()
    if (processedByLti) return

    this.status.setValue('processing')

    const fields = this.cubit.form.fields
    const fieldsObject = Object.fromEntries(fields.map.entries())
    const strictFields = schema.safeParse(fieldsObject)
    if (!strictFields.success) {
      console.error('Failed to parse fields:', strictFields.error)
      this.status.setValue('input')
      return
    }

    const input = strictFields.data
    const sectionId = input.sectionId

    const assignments = input.assignments
    const groupingOptions = input.groupingOptions
    const peerReviewOptions = input.peerReviewOptions
    const gradingOptions = input.gradingOptions

    // Check the whole plan fits the section pass before creating any, so a
    // blocked publish never leaves half a plan assigned.
    this.limitError.setValue(null)
    try {
      await assertCanPublishAssignments(this.cubit.repository, {
        sectionId,
        adding: assignments.length,
      })
    } catch (error) {
      if (!isAssignmentLimitReachedError(error)) throw error
      this.limitError.setValue(error)
      this.status.setValue('input')
      return
    }

    for (const assignment of assignments) {
      const constraints =
        this.cubit.schedule.groupSizeConstraintsBySlideDeckId.get(
          assignment.slideDeckId
        )

      // Role constraints are a floor, not a replacement — getGroupSizeConstraintsFromRoles
      // returns {min:1} for role-less decks, which would otherwise silently clobber the
      // instructor's chosen minimum.
      const groupingSizeMinimum: number = Math.max(
        constraints?.min ?? 1,
        groupingOptions.groupingSizeMinimum
      )
      const groupingSizeMaximum: number =
        assignment.groupSizeOverride ?? groupingOptions.groupingSizeMaximum
      const groupingSize: number =
        assignment.groupSizeOverride ?? groupingOptions.groupingSize

      // if we allowed peer review globally, but override an assignment with role
      // constraints to a grouping size of 1, disable peer review for that assignment
      const peerReviewEnabled =
        peerReviewOptions?.peerReviewsEnabled && groupingSize > 1

      const params = {
        sectionId: sectionId,
        assignmentType: AssignmentType.ManyGroups,
        assignedAt: assignment.startTime,
        expiresAt: assignment.endTime,
        groupingType: groupingOptions.groupingType,
        slideDeckId: assignment.slideDeckId,
        catalogId: assignment.catalogId,
        groupingSizeMinimum,
        groupingSizeMaximum,
        groupingSize,
        assignmentGradingScalars: gradingOptions.configureGrading
          ? this.buildCorrectScalars(
              assignment.slideDeckId,
              gradingOptions.scalars
            )
          : undefined,
        completionMetric: gradingOptions.configureGrading
          ? gradingOptions.completionMetric
          : undefined,
        forbidStudentGroupCreation: groupingOptions.forbidStudentGroupCreation,
        forbidStudentGroupScheduling:
          groupingOptions.forbidStudentGroupScheduling,
        forbidStudentGroupJoining: groupingOptions.forbidStudentGroupJoining,
        forbidStudentGroupLeaving: groupingOptions.forbidStudentGroupLeaving,
        peerReviewExcludesSelfReview: peerReviewEnabled
          ? peerReviewOptions.peerReviewExcludesSelfReview
          : undefined,
        peerReviewsHiddenFromReviewees: peerReviewEnabled
          ? peerReviewOptions.peerReviewsHiddenFromReviewees
          : undefined,
      }

      const assignmentId = await this.createSectionAssignment(params)

      if (peerReviewEnabled && assignmentId) {
        await setPeerReviewQuestionsForAssignment(this.cubit.repository, {
          sectionId,
          assignmentId,
          questions: peerReviewOptions.peerReviewCriteria.map(
            (criterion, index) => ({
              prQuestion: criterion.prQuestion,
              prDescription: criterion.prDescription,
              prOrder: index,
            })
          ),
        })
      }
    }

    this.cubit.storage.clear()
    this.status.setValue('complete')
  }

  buildCorrectScalars(
    slideDeckId: string,
    scalars: GradingScalars | undefined
  ) {
    if (scalars === undefined) return undefined

    const controller = this.cubit.schedule
    const consideredFields: Record<keyof GradingScalars, boolean> = {
      participationScore: true,
      quizScore: true,
      totalRubricScore: true,
      attendanceScore: true,
    }
    const consideredKeys = Object.keys(
      consideredFields
    ) as (keyof GradingScalars)[]

    const hasQuizzes = controller.slideDeckHasQuizzes(slideDeckId)
    const hasRubrics = controller.slideDeckHasRubrics(slideDeckId)

    // if has both match, do nothing
    if (hasQuizzes && hasRubrics) return scalars

    // convert to ints to avoid JS floating point issues
    const scalarsAsInts: Record<keyof GradingScalars, number> = {
      participationScore: Math.round(scalars.participationScore * 100),
      quizScore: Math.round(scalars.quizScore * 100),
      totalRubricScore: Math.round(scalars.totalRubricScore * 100),
      attendanceScore: Math.round(scalars.attendanceScore * 100),
    }

    if (!hasQuizzes) {
      consideredFields.quizScore = false
    }

    if (!hasRubrics) {
      consideredFields.totalRubricScore = false
    }

    // 1st pass, calculate total considered and clear unconsidered
    let totalConsidered = 0
    for (const key of consideredKeys) {
      if (consideredFields[key]) {
        totalConsidered += scalarsAsInts[key]
      } else {
        scalarsAsInts[key] = 0
      }
    }

    // Edge case - if we ended up having nothing considered,
    // repair by giving everything equal weight
    if (totalConsidered === 0) {
      // if we have no values, set everything to 0.25
      for (const key of consideredKeys) {
        if (consideredFields[key]) {
          scalarsAsInts[key] = 25
          totalConsidered += 25
        }
      }
    }

    // If we removed some fields from consideration and we're missing scalar values
    // we need to redistribute the missing values equally between the considered fields
    if (totalConsidered < 100) {
      const difference = 100 - totalConsidered

      const toConsider = Object.keys(consideredFields)
        .map((key) => consideredFields[key as keyof GradingScalars])
        .filter(Boolean).length

      // divide equally
      const toAdd = Math.floor(difference / toConsider)
      for (const key of consideredKeys) {
        if (consideredFields[key]) {
          scalarsAsInts[key] += toAdd
        }
      }
      totalConsidered = sum(
        consideredKeys.map((key) =>
          consideredFields[key] ? scalarsAsInts[key] : 0
        )
      )
    }

    // 2nd pass, set the right values
    if (totalConsidered > 0) {
      for (const key of consideredKeys) {
        if (consideredFields[key]) {
          const ratio = scalarsAsInts[key] / totalConsidered
          scalarsAsInts[key] = Math.round(ratio * totalConsidered)
        }
      }
    }

    // 2nd pass, set the right values
    const newTotalValue = sum(Object.values(scalarsAsInts))

    // if we missed total, repair by scaling everything equally
    if (newTotalValue !== 100) {
      const adjustmentFactor = 100 / newTotalValue
      for (const key of consideredKeys) {
        if (consideredFields[key]) {
          scalarsAsInts[key] = Math.round(scalarsAsInts[key] * adjustmentFactor)
        }
      }
    }

    // if still broken, means we encountered rounding issues
    // we'll fix by adding/subtracting the remainder from the largest field
    if (newTotalValue !== 100) {
      const diff = 100 - sum(Object.values(scalarsAsInts))
      let largestKey: keyof GradingScalars = 'participationScore'
      let largestValue = 0
      for (const key of consideredKeys) {
        if (consideredFields[key] && scalarsAsInts[key] > largestValue) {
          largestValue = scalarsAsInts[key]
          largestKey = key
        }
      }
      scalarsAsInts[largestKey] += diff
    }

    // convert to floats before sending it to server - it expects floats
    const scalarsAsFloats: Record<keyof GradingScalars, number> = {
      participationScore: scalarsAsInts.participationScore / 100,
      quizScore: scalarsAsInts.quizScore / 100,
      totalRubricScore: scalarsAsInts.totalRubricScore / 100,
      attendanceScore: scalarsAsInts.attendanceScore / 100,
    }

    return scalarsAsFloats
  }

  async createSectionAssignment(params: {
    sectionId: string
    assignmentType: AssignmentType
    assignedAt: DateTime
    expiresAt: DateTime
    groupingType: AssignmentGroupingType
    slideDeckId: string
    groupingSize?: number
    groupingSizeMinimum?: number
    groupingSizeMaximum?: number
    catalogId?: string
    assignmentGradingScalars?: FirestoreSectionAssignment['assignmentGradingScalars']
    peerReviewExcludesSelfReview?: boolean
    peerReviewsHiddenFromReviewees?: boolean
  }) {
    return createSectionAssignment(this.cubit.repository, { ...params })
  }
}

function withoutUndefined<T>(
  obj: Record<string, T>
): Record<string, Exclude<T, undefined>> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as Record<string, Exclude<T, undefined>>
}
